import React from "react";
import ExcelJS from "exceljs";
import { Button, UncontrolledTooltip } from "reactstrap";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import moment from "moment";

const ExcelExporter = ({ 
  columns, 
  visibleColumns, 
  data, 
  suffixId, 
  moduleName,
  columnFilters = [], // Nouveau prop pour les filtres
  globalFilter = '' // Nouveau prop pour le filtre global
}) => {
  const { t } = useTranslation();
  
  const exportToExcel = () => {
    const workbook = new ExcelJS.Workbook();
    
    // ==================== ONGLET DATA ====================
    const dataWorksheet = workbook.addWorksheet("data");

    // Ajouter des en-têtes de colonne pour les colonnes visibles
    const headers = visibleColumns.map(columnKey => {
      const column = columns.find(col => col.accessorKey === columnKey);
      return column ? (typeof column.header === 'function' ? t(columnKey) : column.header) : '';
    });
    
    // Styliser l'en-tête
    const headerRow = dataWorksheet.addRow(headers);
    headerRow.eachCell((cell) => {
      cell.font = { bold: true };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFD3D3D3' }
      };
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      };
    });

    // Ajouter les données pour les colonnes visibles
    data.forEach(row => {
      const rowData = visibleColumns.map(columnKey => {
        const column = columns.find(col => col.accessorKey === columnKey);
        let cellValue = row[columnKey];
        
        if (column && (column.type === 'date' || column.type === 'dateRangePicker') && cellValue) {
          // Formater la date au format dd/MM/AAAA
          cellValue = format(new Date(cellValue), 'dd/MM/yyyy');
        } else if (column && column.type === 'array' && Array.isArray(cellValue)) {
          // Joindre les éléments du tableau avec des virgules
          cellValue = cellValue.join(', ');
        } else if (column && column.type === 'iconTab' && Array.isArray(cellValue)) {
          // Pour les iconTab, afficher le nombre d'éléments
          cellValue = cellValue.length;
        } else if (column && column.type === 'tag' && cellValue) {
          // Pour les tags, garder la valeur brute
          cellValue = t(cellValue);
        }

        return cellValue;
      });
      dataWorksheet.addRow(rowData);
    });

    // Auto-ajuster la largeur des colonnes
    dataWorksheet.columns.forEach(column => {
      let maxLength = 0;
      column.eachCell({ includeEmpty: true }, (cell) => {
        const columnLength = cell.value ? cell.value.toString().length : 10;
        if (columnLength > maxLength) {
          maxLength = columnLength;
        }
      });
      column.width = maxLength < 10 ? 10 : maxLength + 2;
    });

    // ==================== ONGLET FILTERS ====================
    const filtersWorksheet = workbook.addWorksheet("filters");
    
    // En-têtes pour l'onglet filtres
    const filterHeaders = [t('filterName'),  t('filterValue')];
    const filterHeaderRow = filtersWorksheet.addRow(filterHeaders);
    
    // Styliser l'en-tête des filtres
    filterHeaderRow.eachCell((cell) => {
      cell.font = { bold: true };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFB8CCE4' }
      };
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      };
    });

    // Ajouter le filtre global s'il existe
    if (globalFilter && globalFilter.trim() !== '') {
      filtersWorksheet.addRow([
        t('globalSearch'),
        globalFilter
      ]);
    }

    // Ajouter les filtres de colonnes
    columnFilters.forEach(filter => {
      const column = columns.find(col => col.accessorKey === filter.id);
      const columnName = column ? (typeof column.header === 'function' ? t(filter.id) : column.header) : filter.id;
      
      let filterValue = '';
      
      if (column) {
        // Déterminer le type de filtre
        if (column.type === 'select' || column.type === 'selectArray' || column.type === 'selectIcon') {
          if (Array.isArray(filter.value)) {
            filterValue = filter.value.map(val => t(val)).join(', ');
          } else {
            filterValue = t(filter.value);
          }
        } else if (column.type === 'dateRangePicker') {
          if (filter.value && typeof filter.value === 'object') {
            const startDate = filter.value.start ? moment(filter.value.start).format('DD/MM/YYYY') : '';
            const endDate = filter.value.end ? moment(filter.value.end).format('DD/MM/YYYY') : '';
            filterValue = `${startDate} - ${endDate}`;
          }
        } else if (column.type === 'date') {
          filterValue = filter.value ? moment(filter.value).format('DD/MM/YYYY') : '';
        } else {
          filterValue = filter.value || '';
        }
      }
      
      filtersWorksheet.addRow([
        columnName,
        filterValue
      ]);
    });

    // Si aucun filtre n'est appliqué, ajouter une ligne informative
    if (columnFilters.length === 0 && (!globalFilter || globalFilter.trim() === '')) {
      filtersWorksheet.addRow([
        t('noFiltersApplied'),
        '',
        ''
      ]);
    }

    // Auto-ajuster la largeur des colonnes pour l'onglet filtres
    filtersWorksheet.columns.forEach(column => {
      let maxLength = 0;
      column.eachCell({ includeEmpty: true }, (cell) => {
        const columnLength = cell.value ? cell.value.toString().length : 10;
        if (columnLength > maxLength) {
          maxLength = columnLength;
        }
      });
      column.width = maxLength < 15 ? 15 : maxLength + 2;
    });

    // Télécharger le fichier Excel
    workbook.xlsx.writeBuffer().then(buffer => {
      const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      const formattedDate = moment().format('DD-MM-YYYY');
      a.href = url;
      a.download = `${t("export")}_${t(moduleName)}_${formattedDate}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    });
  };

  return (
    <>
      <Button
        type="button"
        id={"projExcelBtn"+suffixId}
        className={`btnTable`}
        onClick={exportToExcel}
        data-cy={"table-button-export"}
      >
        <i className="mdi mdi-microsoft-excel iconOptions"></i>
      </Button>
      <UncontrolledTooltip
        transition={{ timeout: 300 }}
        placement="top"
        fade={false}
        target={"projExcelBtn"+suffixId}
      >
        {t('exportTable')}
      </UncontrolledTooltip>
    </>
  );
};

export default ExcelExporter;